import {useState, useRef, useEffect, useCallback} from 'react'
import {CurrencyInputProps} from 'react-currency-input-field'

export const InputCurrency: React.FC<CurrencyInputProps> = ({
  className,
  intlConfig,
  value,
  onValueChange,
  disabled,
  placeholder,
  ...props
}) => {
  const intlConfigInside = intlConfig || {locale: 'en-US', currency: 'USD'}
  const [displayValue, setDisplayValue] = useState<string>('0.00')
  const inputRef = useRef<HTMLInputElement>(null)
  const internalValueRef = useRef<number>(0) // valor en centavos

  // Convierte centavos a string formateado
  const formatValue = useCallback(
    (cents: number): string => {
      const dollars = cents / 100
      return new Intl.NumberFormat(intlConfigInside.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(dollars)
    },
    [intlConfigInside.locale]
  )

  // Inicializa el valor cuando cambia externamente
  useEffect(() => {
    if (value !== undefined && value !== null && !Array.isArray(value)) {
      const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : Number(value)
      if (!isNaN(numValue) && isFinite(numValue)) {
        const cents = Math.round(numValue * 100)
        internalValueRef.current = cents
        setDisplayValue(formatValue(cents))
      }
    } else {
      internalValueRef.current = 0
      setDisplayValue('0.00')
    }
  }, [value, formatValue])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current
    const hasSelection = input && input.selectionStart !== input.selectionEnd
    const isFullySelected = hasSelection && input.selectionStart === 0 && input.selectionEnd === input.value.length

    // Permitir: backspace, delete, tab, escape, enter
    if (
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'Tab' ||
      e.key === 'Escape' ||
      e.key === 'Enter' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault()
        // Si todo el texto está seleccionado, resetear a 0.00
        if (isFullySelected) {
          internalValueRef.current = 0
          const newDisplay = formatValue(0)
          setDisplayValue(newDisplay)
          if (onValueChange) {
            onValueChange('0', undefined, {
              float: 0,
              formatted: newDisplay,
              value: '0',
            })
          }
        } else {
          // Comportamiento normal: eliminar último dígito
          const newCents = Math.floor(internalValueRef.current / 10)
          internalValueRef.current = newCents
          const newDisplay = formatValue(newCents)
          setDisplayValue(newDisplay)
          if (onValueChange) {
            onValueChange(String(newCents / 100), undefined, {
              float: newCents / 100,
              formatted: newDisplay,
              value: String(newCents / 100),
            })
          }
        }
      }
      return
    }

    // Permitir Ctrl/Cmd + A, C, V, X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return
    }

    // Solo permitir dígitos
    if (!/^\d$/.test(e.key)) {
      e.preventDefault()
      return
    }

    e.preventDefault()
    const digit = parseInt(e.key)

    // Si todo el texto está seleccionado, empezar desde el dígito ingresado
    let newCents: number
    if (isFullySelected) {
      newCents = digit
    } else {
      newCents = internalValueRef.current * 10 + digit
    }

    // Limitar a un valor razonable (999,999,999.99)
    if (newCents > 99999999999) {
      return
    }

    internalValueRef.current = newCents
    const newDisplay = formatValue(newCents)
    setDisplayValue(newDisplay)

    if (onValueChange) {
      onValueChange(String(newCents / 100), undefined, {
        float: newCents / 100,
        formatted: newDisplay,
        value: String(newCents / 100),
      })
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')

    // Remover todo excepto dígitos y punto decimal
    const cleaned = pastedText.replace(/[^\d.]/g, '')
    const numValue = parseFloat(cleaned)

    if (!isNaN(numValue)) {
      const cents = Math.round(numValue * 100)
      if (cents <= 99999999999) {
        internalValueRef.current = cents
        const newDisplay = formatValue(cents)
        setDisplayValue(newDisplay)
        if (onValueChange) {
          onValueChange(String(cents / 100), undefined, {
            float: cents / 100,
            formatted: newDisplay,
            value: String(cents / 100),
          })
        }
      }
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Seleccionar todo el texto al hacer foco
    e.target.select()
  }

  return (
    <input
      ref={inputRef}
      type="text"
      className={`form-control ${className}`}
      value={displayValue}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={handleFocus}
      disabled={disabled}
      placeholder={placeholder || '0.00'}
      {...props}
      onChange={() => {}} // Prevenir warning, el cambio se maneja en onKeyDown
    />
  )
}
